import { BasicButton, FileUpload } from "@certn/cascadia-react";
import { CascadiaControlProps } from "@certn/forms";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { useTranslation } from "react-i18next";

import { tryOpenDocumentInNewTab } from "./utils";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { dataToFile, fileToData } from "../../utils";

// FIRE-134: scope the `maxSizeBytes` schema option to specific claims. Other
// sections (NBIDV identity documents, quickscreen general authorization
// consent) already set `maxSizeBytes` in their ui schema options, but this
// control does not honor it for them. Turning it on universally would tighten
// limits on flows whose owners have not signed off on enforcement.
//
// The identity-document modal is a nested form whose data is one document.
// UploadControl then receives scope `#/properties/files` and path `files`,
// with no `identity_document_files` segment. That pair is how the 10MB
// InformData limit is opted in. A root-level scope or path that still
// contains the claim name is covered too.
// Consent claim names come from:
//   - `project/engine/check_definitions/claim_types/auradata_consent_form.py`
//   - `project/engine/check_definitions/claim_types/hedd_consent_form.py`
const MAX_SIZE_BYTES_OPT_IN_CLAIMS = [
  "auradata_consent_form",
  "hedd_consent_form",
];
const IDENTITY_DOCUMENT_FILES_CLAIM = "identity_document_files";
const IDENTITY_DOCUMENT_MODAL_SCOPE = "#/properties/files";
const IDENTITY_DOCUMENT_MODAL_PATH = "files";

function isIdentityDocumentFilesUpload(
  scope: string | undefined,
  path: string | undefined
) {
  if ((scope ?? "").includes(IDENTITY_DOCUMENT_FILES_CLAIM)) {
    return true;
  }
  if ((path ?? "").includes(IDENTITY_DOCUMENT_FILES_CLAIM)) {
    return true;
  }
  return (
    scope === IDENTITY_DOCUMENT_MODAL_SCOPE &&
    path === IDENTITY_DOCUMENT_MODAL_PATH
  );
}

function resolveMaxSizeBytes(
  uischema: CascadiaControlProps["uischema"],
  path?: string
) {
  const scope: string | undefined = uischema?.scope;
  const optedInByScope =
    !!scope &&
    MAX_SIZE_BYTES_OPT_IN_CLAIMS.some((claim) => scope.endsWith(claim));
  if (!optedInByScope && !isIdentityDocumentFilesUpload(scope, path)) {
    return undefined;
  }
  const fromOptions = uischema?.options?.maxSizeBytes;
  return typeof fromOptions === "number" ? fromOptions : undefined;
}

export function UploadControl(props: CascadiaControlProps) {
  const { t } = useTranslation();
  const { data, label, options, path, uischema } = props;
  const { generatePresignedUrlEndpoint, downloadUrl } =
    useApplicationFormContext();
  const multiple = uischema.options?.multiple;
  const maxSizeBytes = resolveMaxSizeBytes(uischema, path);

  const buttonLabel = label
    ? t("Add {{label}}", { label: label.toLowerCase() })
    : t("Add document");
  const itemSubtitle = options.itemSubtitle || undefined;
  const shouldShowMainLabel = options.showMainLabel;

  const files = Array.isArray(data)
    ? data.map((file) => {
        return dataToFile(file, itemSubtitle);
      })
    : [dataToFile(data, itemSubtitle)];

  const tryOpenFile = (fileKey: string | undefined) => {
    tryOpenDocumentInNewTab(downloadUrl, fileKey);
  };

  return (
    <FileUpload
      label={shouldShowMainLabel === false ? undefined : label}
      triggerButton={
        uischema.options?.showTriggerButton === false ? undefined : (
          <BasicButton leftIcon="add">{buttonLabel}</BasicButton>
        )
      }
      iconType="image"
      iconColor="icon-base"
      description={props.description}
      isRequired={props.required}
      isDisabled={!props.enabled}
      isInvalid={props.invalid}
      onBlur={props.setTouched}
      bucketDirectory={uischema.options?.bucketDirectory}
      maxSizeBytes={maxSizeBytes}
      requestUrl={generatePresignedUrlEndpoint}
      multiple={multiple}
      files={files.filter(Boolean)}
      setFiles={(files) => {
        props.setValue(multiple ? files.map(fileToData) : fileToData(files[0]));
      }}
      errors={props.errors}
      allowedFileTypes={DEFAULT_FILE_TYPES}
      downloadButtonInEditState={options.downloadButtonInEditState}
      tryOpenFile={tryOpenFile}
    />
  );
}
